/**
 * Leaderboard celebration – confetti for top 3/10, motivation for others.
 * Per-tournament suppress; auto-reset when position changes.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useGame } from './GameContext';
import LeaderboardCelebrationModal from '../components/LeaderboardCelebrationModal';
import PositionChangeToast from '../components/PositionChangeToast';
import * as readApi from '../api/readApi';
import { userUpdateProfile } from '../api/userApi';
import confetti from 'canvas-confetti';

const LeaderboardCelebrationContext = createContext();

export function useLeaderboardCelebration() {
  const ctx = useContext(LeaderboardCelebrationContext);
  if (!ctx) return null;
  return ctx;
}

/** Find user's rank in singles or doubles rankings. Returns { rank (1-based), row } or null. */
function findUserRank(rankings, userId, isDoubles) {
  if (!rankings || !userId) return null;
  const idx = rankings.findIndex((r) => {
    if (isDoubles) {
      const ids = r.playerIds || [];
      return ids.includes(userId);
    }
    return r.playerId === userId;
  });
  if (idx < 0) return null;
  return { rank: idx + 1, row: rankings[idx] };
}

/** Check if position changed vs stored. Returns { changed, prevSingles, prevDoubles, currSingles, currDoubles } */
function detectPositionChange(prev, curr) {
  if (!prev && !curr) return { changed: false };
  const prevS = prev?.singles ?? null;
  const prevD = prev?.doubles ?? null;
  const currS = curr?.singles ?? null;
  const currD = curr?.doubles ?? null;
  const changed = prevS !== currS || prevD !== currD;
  return { changed, prevSingles: prevS, prevDoubles: prevD, currSingles: currS, currDoubles: currD };
}

export function LeaderboardCelebrationProvider({ children }) {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const { gameId } = useGame();
  const [modalState, setModalState] = useState(null); // { type: 'confetti'|'motivation', tournamentId, tournamentName, rank, ptsFor, ptsToThird, nextMatch, mode }
  const [positionChange, setPositionChange] = useState(null); // { tournamentId, tournamentName, prevSingles, prevDoubles, currSingles, currDoubles }
  const hasCheckedThisSession = useRef(new Set()); // tournamentIds we've already shown for this session

  const suppressForTournament = useCallback(
    async (tournamentId) => {
      if (!currentUser?.uid || !tournamentId) return;
      const suppressed = { ...(userProfile?.leaderboardCelebrationSuppressed || {}), [tournamentId]: true };
      try {
        await userUpdateProfile(currentUser.uid, { leaderboardCelebrationSuppressed: suppressed });
        await refreshUserProfile();
      } catch (e) {
        console.error('[LeaderboardCelebration] Failed to suppress:', e);
      }
    },
    [currentUser?.uid, userProfile?.leaderboardCelebrationSuppressed, refreshUserProfile]
  );

  const updatePosition = useCallback(
    async (tournamentId, newPosition, clearSuppress = false) => {
      if (!currentUser?.uid || !tournamentId) return;
      const byTournament = { ...(userProfile?.leaderboardPositionByTournament || {}) };
      byTournament[tournamentId] = newPosition;
      const payload = { leaderboardPositionByTournament: byTournament };
      if (clearSuppress) {
        const suppressed = { ...(userProfile?.leaderboardCelebrationSuppressed || {}) };
        delete suppressed[tournamentId];
        payload.leaderboardCelebrationSuppressed = Object.keys(suppressed).length ? suppressed : {};
      }
      try {
        await userUpdateProfile(currentUser.uid, payload);
        await refreshUserProfile();
      } catch (e) {
        console.error('[LeaderboardCelebration] Failed to update position:', e);
      }
    },
    [currentUser?.uid, userProfile?.leaderboardPositionByTournament, userProfile?.leaderboardCelebrationSuppressed, refreshUserProfile]
  );

  const updatePositionAndClearSuppress = useCallback(
    (tournamentId, newPosition) => updatePosition(tournamentId, newPosition, true),
    [updatePosition]
  );

  const checkAndShowCelebration = useCallback(
    async (options = {}) => {
      const { forceCheck = false, afterMatchComplete = false, tournamentId: explicitTournamentId } = options;
      if (!currentUser?.uid) return;

      try {
        const dashboard = await readApi.getDashboardData(gameId);
        const tournament = dashboard.activeTournament || null;
        const isRegistered = dashboard.isRegistered ?? false;
        const tournamentId = explicitTournamentId || tournament?.id;
        if (!tournamentId || !isRegistered) return;

        // Already showed for this tournament this session (unless after match or force)
        if (!forceCheck && !afterMatchComplete && hasCheckedThisSession.current.has(tournamentId)) return;

        const stats = await readApi.getStatisticsData(gameId, { tournamentId, part: 'rankings' });
        const rankingsSingles = stats?.rankingsSingles ?? [];
        const rankingsDoubles = stats?.rankingsDoubles ?? [];

        const sing = findUserRank(rankingsSingles, currentUser.uid, false);
        const doub = findUserRank(rankingsDoubles, currentUser.uid, true);
        const currPosition = {
          singles: sing?.rank ?? null,
          doubles: doub?.rank ?? null,
        };
        const prevPosition = userProfile?.leaderboardPositionByTournament?.[tournamentId] || null;

        // Position change: clear suppress and show toast; also store position when first seen
        const { changed } = detectPositionChange(prevPosition, currPosition);
        if (changed) {
          await updatePositionAndClearSuppress(tournamentId, currPosition);
          setPositionChange({
            tournamentId,
            tournamentName: tournament?.name || 'Tournament',
            ...detectPositionChange(prevPosition, currPosition),
          });
        } else if (!prevPosition && (currPosition.singles || currPosition.doubles)) {
          await updatePosition(tournamentId, currPosition, false);
        }

        // After match: we showed position change toast; skip celebration modal for this run only.
        // Do NOT add to hasCheckedThisSession so the user still gets confetti/stats when they visit HomeFeed next.
        if (afterMatchComplete) return;

        const suppressed = userProfile?.leaderboardCelebrationSuppressed?.[tournamentId] === true;
        if (suppressed && !changed) return;

        hasCheckedThisSession.current.add(tournamentId);

        const rank = sing?.rank ?? doub?.rank ?? null;
        const mode = sing ? 'singles' : 'doubles';
        const row = sing?.row ?? doub?.row;
        const ptsFor = row?.ptsFor ?? 0;

        let ptsToThird = null;
        if (mode === 'singles' && rankingsSingles.length >= 3) {
          ptsToThird = Math.max(0, (rankingsSingles[2]?.ptsFor ?? 0) - ptsFor);
        } else if (mode === 'doubles' && rankingsDoubles.length >= 3) {
          ptsToThird = Math.max(0, (rankingsDoubles[2]?.ptsFor ?? 0) - ptsFor);
        }

        const tournamentMatches = dashboard.tournamentMatches || [];
        const myNextMatch = tournamentMatches.find((m) => {
          if (m.status === 'completed') return false;
          if (m.matchType === 'doubles') {
            const t1 = m.team1Players || [];
            const t2 = m.team2Players || [];
            return t1.includes(currentUser.uid) || t2.includes(currentUser.uid);
          }
          return m.player1Id === currentUser.uid || m.player2Id === currentUser.uid;
        });

        const isTopThree = rank != null && rank <= 3;
        const isCloseTopTen = rank != null && rank >= 4 && rank <= 10;

        if (isTopThree || isCloseTopTen) {
          setModalState({
            type: 'confetti',
            tournamentId,
            tournamentName: tournament?.name || 'Tournament',
            rank,
            ptsFor,
            ptsToThird,
            nextMatch: myNextMatch || null,
            mode,
          });
        } else if (rank != null) {
          setModalState({
            type: 'motivation',
            tournamentId,
            tournamentName: tournament?.name || 'Tournament',
            rank,
            ptsFor,
            ptsToThird,
            nextMatch: myNextMatch || null,
            mode,
          });
        } else {
          await updatePosition(tournamentId, currPosition, false);
        }
      } catch (e) {
        console.error('[LeaderboardCelebration] checkAndShowCelebration error:', e);
      }
    },
    [currentUser?.uid, gameId, userProfile?.leaderboardCelebrationSuppressed, userProfile?.leaderboardPositionByTournament, updatePosition, updatePositionAndClearSuppress]
  );

  const fireConfetti = useCallback(() => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#eab308', '#f59e0b', '#2563eb', '#16a34a', '#dc2626'];

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, []);

  const dismissModal = useCallback(() => setModalState(null), []);
  const dismissPositionChange = useCallback(() => setPositionChange(null), []);

  const value = {
    modalState,
    positionChange,
    suppressForTournament,
    checkAndShowCelebration,
    fireConfetti,
    dismissModal,
    dismissPositionChange,
    updatePositionAndClearSuppress,
  };

  return (
    <LeaderboardCelebrationContext.Provider value={value}>
      {children}
      <LeaderboardCelebrationModal />
      <PositionChangeToast />
    </LeaderboardCelebrationContext.Provider>
  );
}

